import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { api, userID } from "../../Services/api";
import { IUserADM, IUserColab, IUserADMLogin } from "../../Interfaces";
import { IUserRegister } from "../../Interfaces";
import { History } from "history";
import { toast } from "react-toastify";

interface UserProviderData {
  auth: string;
  currentUser: IUserColab | IUserADM | undefined;
  login: (data: IUserADMLogin, history: History, redirect: string) => void;
  registerUser: (data: IUserRegister) => void;
  logout: () => void;
  admAuth: boolean;
  colabList: IUserColab[];
  deleteColab: (colab: IUserColab) => void;
  editColab: (colab: IUserColab | IUserADM) => void;
  getColabs: () => void;
  isLoading: boolean;
  setIsLoading: (active: boolean) => void;
}

interface UserProviderProps {
  children: ReactNode;
}

export const UserContext = createContext<UserProviderData>(
  {} as UserProviderData
);

export const UserProvider = ({ children }: UserProviderProps) => {
  const token = localStorage.getItem("authToken") || "";
  const [colabList, setColabList] = useState<IUserColab[]>([]);
  const [admAuth, setAdmAuth] = useState<boolean>(false);
  const [currentUser, setCurrentUser] = useState<
    IUserColab | IUserADM | undefined
  >();
  const [auth, setAuth] = useState<string>(token);
  const [fireUseEffect, setFireUseEffect] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const login = (data: IUserADMLogin, history: History, redirect: string) => {
    setIsLoading(true);
    api
      .post("login", data)
      .then((response) => {
        localStorage.setItem("authToken", response.data.accessToken);
        setAuth(response.data.accessToken);
        window.location.reload();
        toast.success("Bem vindo");
        setIsLoading(false);
      })
      .then((_) => history.push(redirect))
      .catch((_) => toast.error("Email e/ou senha inválidos"));
  };

  const registerUser = (data: IUserRegister) => {
    api
      .post("register", data)
      .then((_) => {
        getColabs();
        toast.success("Colaborador registrado");
      })
      .catch((_) => toast.error("Erro ao registrar usuário"));
  };

  const logout = () => {
    localStorage.clear();
    window.location.reload();
    setAuth("");
    toast.success("Até mais");
  };

  const isAdmCheck = useCallback(() => {
    if (currentUser?.isAdm) {
      return setAdmAuth(true);
    } else return setAdmAuth(false);
  }, [currentUser?.isAdm]);

  useEffect(() => {
    setIsLoading(true);
    api
      .get(`/users/${userID}`)
      .then((response) => setCurrentUser(response.data))
      .then((_) => setIsLoading(false));
  }, [auth]);

  useEffect(() => {
    isAdmCheck();
  }, [isAdmCheck, currentUser]);

  useEffect(() => {
    setIsLoading(true);
    api
      .get("/users")
      .then((response) =>
        setColabList(
          response.data.filter(
            (colab: IUserColab | IUserADM) => colab.isAdm === false
          )
        )
      )
      .then((_) => setIsLoading(false))
      .catch((e) => e);
  }, []);

  //GET COLABS//

  const getColabs = () => {
    setFireUseEffect(true);
  };

  useEffect(() => {
    setIsLoading(true);
    if (fireUseEffect) {
      api
        .get("/users")
        .then((res) => {
          setColabList(res.data.filter((el: any) => !el.isAdm));
          setFireUseEffect(false);
        })
        .then((_) => setIsLoading(false));
    }
  }, [fireUseEffect]);

  //GET COLABS//

  const deleteColab = (colab: IUserColab) => {
    const { id } = colab;
    api
      .delete(`/users/${id}`)
      .then((_) => {
        getColabs();
        toast.success("Colaborador excluido");
      })
      .catch((_) => toast.error("Erro ao excluir colaborador"));
  };

  const editColab = (colab: IUserColab | IUserADM) => {
    const { id } = colab;
    api
      .patch(`/users/${id}`, colab)
      .then((_) => {
        getColabs();
        toast.success("Colaborador excluido");
      })
      .catch((_) => toast.error("Erro ao excluir colaborador"));
  };

  return (
    <UserContext.Provider
      value={{
        auth,
        currentUser,
        login,
        registerUser,
        logout,
        admAuth,
        colabList,
        deleteColab,
        editColab,
        getColabs,
        isLoading,
        setIsLoading,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => useContext(UserContext);
